import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Sparkles, Clock, User as UserIcon, Building, MapPin, Wallet, Link as LinkIcon, Linkedin, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AIEmailComposer from "@/components/AIEmailComposer";
import ShortlistManager from "@/components/ShortlistManager";
import LinkedInEnrichDialog from "@/components/LinkedInEnrichDialog";

const STAGES = ["New", "Contacted", "Qualified", "Meeting", "Proposal", "Won", "Lost"];

export default function LeadDrawer({ lead, onClose, onChange }) {
  const [activities, setActivities] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [current, setCurrent] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [shortlistOpen, setShortlistOpen] = useState(false);
  const [linkedinOpen, setLinkedinOpen] = useState(false);

  useEffect(() => {
    if (!lead) return;
    setCurrent(lead);
    if (lead._openAI) setAiOpen(true);
    (async () => {
      const res = await api.get(`/leads/${lead.lead_id}/activities`);
      setActivities(res.data);
    })();
  }, [lead]);

  if (!lead) return null;

  const updateStage = async (v) => {
    const res = await api.patch(`/leads/${lead.lead_id}`, { stage: v });
    setCurrent(res.data);
    onChange?.();
    toast.success(`Moved to ${v}`);
    const acts = await api.get(`/leads/${lead.lead_id}/activities`);
    setActivities(acts.data);
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    await api.post(`/leads/${lead.lead_id}/activities`, { lead_id: lead.lead_id, kind: "note", content: newNote });
    setNewNote("");
    const acts = await api.get(`/leads/${lead.lead_id}/activities`);
    setActivities(acts.data);
  };

  return (
    <>
      <Sheet open={!!lead} onOpenChange={(o) => !o && onClose()}>
        <SheetContent data-testid="lead-drawer" className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <div className="label-eyebrow">{current?.segment}</div>
            <SheetTitle className="font-serif-display text-3xl text-[#0A1128] flex items-center gap-3">
              {current?.picture_url && (
                <img src={current.picture_url} alt="" className="w-12 h-12 rounded-full object-cover border border-[#E2E8F0]" />
              )}
              <span>{current?.name}</span>
            </SheetTitle>
            {current?.title && <div className="text-sm text-slate-600 mt-1">{current.title}{current.company ? ` · ${current.company}` : ""}</div>}
          </SheetHeader>

          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-slate-600"><Mail className="w-4 h-4 text-[#C5A880]" />{current?.email || "—"}</div>
              <div className="flex items-center gap-2 text-slate-600"><UserIcon className="w-4 h-4 text-[#C5A880]" />{current?.phone || "—"}</div>
              <div className="flex items-center gap-2 text-slate-600"><Building className="w-4 h-4 text-[#C5A880]" />{current?.company || "—"}</div>
              <div className="flex items-center gap-2 text-slate-600"><MapPin className="w-4 h-4 text-[#C5A880]" />{current?.location || "—"}</div>
              <div className="flex items-center gap-2 text-slate-600 col-span-2"><Wallet className="w-4 h-4 text-[#C5A880]" />Budget: {current?.budget_sgd ? `S$${new Intl.NumberFormat().format(current.budget_sgd)}` : "—"} · {current?.net_worth_range || "N/A"}</div>
            </div>

            {current?.notes && (
              <div className="text-sm text-slate-700 bg-[#F9FAFB] border border-[#E2E8F0] rounded-md p-4">{current.notes}</div>
            )}

            <div className="flex items-center gap-3">
              <div className="label-eyebrow">Stage</div>
              <Select value={current?.stage} onValueChange={updateStage}>
                <SelectTrigger data-testid="drawer-stage-select" className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Button
                data-testid="drawer-ai-btn"
                onClick={() => setAiOpen(true)}
                className="ml-auto bg-[#C5A880] text-[#0A1128] hover:bg-[#D4C09E]"
                disabled={!current?.email}
              >
                <Sparkles className="w-4 h-4 mr-2" /> AI compose email
              </Button>
            </div>

            <div>
              <Button
                data-testid="drawer-shortlist-btn"
                variant="outline"
                onClick={() => setShortlistOpen(true)}
                className="w-full border-[#E2E8F0]"
              >
                <LinkIcon className="w-4 h-4 mr-2 text-[#C5A880]" /> Manage shortlist &amp; private link
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button
                data-testid="drawer-linkedin-btn"
                variant="outline"
                onClick={() => setLinkedinOpen(true)}
                className="border-[#E2E8F0]"
              >
                <Linkedin className="w-4 h-4 mr-2 text-[#0A66C2]" /> Enrich from LinkedIn
              </Button>
              <a
                data-testid="drawer-sri-btn"
                href={`https://plus.sri.sg/?q=${encodeURIComponent((current?.company || current?.name || "").trim())}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-[#E2E8F0] rounded-md h-10 px-4 text-sm text-slate-800 hover:bg-[#F9FAFB] transition-colors"
              >
                Search on SRI Polaris <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

            <div>
              <div className="label-eyebrow mb-2">Add note</div>
              <Textarea data-testid="drawer-note-input" value={newNote} onChange={(e) => setNewNote(e.target.value)} rows={3} placeholder="Meeting notes, follow-ups, context…" />
              <Button data-testid="drawer-note-add" onClick={addNote} className="mt-2 bg-[#0A1128] text-white hover:bg-[#152243]" size="sm">Add note</Button>
            </div>

            <div>
              <div className="label-eyebrow mb-3 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Activity log</div>
              <div className="space-y-3">
                {activities.length === 0 && <div className="text-sm text-slate-500">No activity yet.</div>}
                {activities.map((a) => (
                  <div key={a.activity_id} className="border-l-2 border-[#C5A880] pl-4 py-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-[10px] rounded-full">{a.kind}</Badge>
                      <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-slate-800 mt-1">{a.content}</div>
                    {a.author && <div className="text-xs text-slate-400 mt-0.5">— {a.author}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AIEmailComposer
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        lead={current}
        onSent={async () => {
          const acts = await api.get(`/leads/${lead.lead_id}/activities`);
          setActivities(acts.data);
          onChange?.();
        }}
      />
      <ShortlistManager
        open={shortlistOpen}
        onClose={() => setShortlistOpen(false)}
        lead={current}
      />
      <LinkedInEnrichDialog
        open={linkedinOpen}
        onClose={() => setLinkedinOpen(false)}
        lead={current}
        onApplied={async () => {
          const fresh = await api.get(`/leads/${lead.lead_id}`);
          setCurrent(fresh.data);
          const acts = await api.get(`/leads/${lead.lead_id}/activities`);
          setActivities(acts.data);
          onChange?.();
        }}
      />
    </>
  );
}
